import { useEffect, useState } from 'react';
import {
  Paper, Typography, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Button, Stack, Chip, MenuItem, Alert, Snackbar, Divider, Select, OutlinedInput, Box, IconButton,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { navForRole } from '../components/officerNav';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const RECOMMENDATIONS = [
  { value: 'AWARD', label: 'Award to recommended vendor' },
  { value: 'REJECT_ALL', label: 'Reject all bids' },
  { value: 'RETENDER', label: 'Re-tender' },
];

const naira = (n) => (n == null ? '—' : '₦' + Number(n).toLocaleString('en-NG'));



export default function EvaluationWorkspace() {
  const { tenderId } = useParams();
  const [tab, setTab] = useState(0);
  const [ws, setWs] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [summary, setSummary] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get(`/tenders/${tenderId}/evaluation`);
    setWs(data.data);
    if (data.data.report) {
      setSummary(data.data.report.summary || '');
      setRecommendation(data.data.report.recommendation || '');
    }
  };
  const loadComparison = async () => {
    const { data } = await api.get(`/tenders/${tenderId}/evaluation/comparison`);
    setComparison(data.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch((e) => setToast(e.response?.data?.error?.message || 'Could not load')); }, [tenderId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 1 || tab === 2) loadComparison().catch(() => {}); }, [tab]);

  const criteria = ws?.criteria || [];

  const setScore = (bidId, critId, value) => {
    setWs((prev) => ({
      ...prev,
      bids: prev.bids.map((b) => b.bidId === bidId
        ? { ...b, technicalScores: { ...b.technicalScores, [critId]: value } } : b),
    }));
  };
  const setComment = (bidId, value) => {
    setWs((prev) => ({ ...prev, bids: prev.bids.map((b) => b.bidId === bidId ? { ...b, technicalComment: value } : b) }));
  };
  const setAmount = (bidId, value) => {
    setWs((prev) => ({ ...prev, bids: prev.bids.map((b) => b.bidId === bidId ? { ...b, bidAmount: value } : b) }));
  };

  const saveTechnical = async (bid) => {
    try {
      await api.post(`/tenders/${tenderId}/evaluation/technical`, { bidId: bid.bidId, scores: bid.technicalScores, comment: bid.technicalComment });
      setToast('Technical scores saved');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Save failed'); }
  };
  const saveFinancial = async (bid) => {
    try {
      await api.post(`/tenders/${tenderId}/evaluation/financial`, { bidId: bid.bidId, bidAmount: bid.bidAmount });
      setToast('Bid amount saved');
      loadComparison();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Save failed'); }
  };
  const saveReport = async (submit) => {
    try {
      await api.post(`/tenders/${tenderId}/evaluation/report`, { summary, recommendation, submit });
      setToast(submit ? 'Evaluation report submitted' : 'Report saved');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Save failed'); }
  };

  // --- Evaluator assignment (officers/admins only) ---
  const { user } = useAuth();
  const canAssign = user?.role === 'MDA_OFFICER' || user?.role === 'ADMIN';
  const [assigned, setAssigned] = useState([]);
  const [allEvaluators, setAllEvaluators] = useState([]);
  const [toAdd, setToAdd] = useState([]);
  const loadAssignments = async () => {
    if (!canAssign) return;
    try {
      const [a, e] = await Promise.all([
        api.get(`/tenders/${tenderId}/evaluators`),
        api.get('/evaluators'),
      ]);
      setAssigned(a.data.data);
      setAllEvaluators(e.data.data);
    } catch { /* ignore */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAssignments(); }, [tenderId, canAssign]);
  const assignEvaluators = async () => {
    if (toAdd.length === 0) return;
    try {
      await api.post(`/tenders/${tenderId}/evaluators`, { evaluatorIds: toAdd });
      setToAdd([]);
      setToast('Evaluators assigned');
      loadAssignments();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Assign failed'); }
  };
  const removeEvaluator = async (evaluatorId) => {
    try {
      await api.delete(`/tenders/${tenderId}/evaluators/${evaluatorId}`);
      setToast('Evaluator removed');
      loadAssignments();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Remove failed'); }
  };
  const unassignedOptions = allEvaluators.filter((e) => !assigned.some((a) => a.id === e.id));
  return (
    <DashboardLayout nav={navForRole(user?.role)} welcomeName={user?.fullName} title="Evaluation Workspace">
      {canAssign && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Assigned Evaluators</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Only assigned evaluators can access this tender's evaluation.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {assigned.length === 0 && <Typography variant="body2" color="text.secondary">None assigned yet.</Typography>}
            {assigned.map((a) => (
              <Chip key={a.id} label={a.fullName} onDelete={() => removeEvaluator(a.id)} />
            ))}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <Select
              multiple
              displayEmpty
              size="small"
              value={toAdd}
              onChange={(e) => setToAdd(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput />}
              renderValue={(sel) => sel.length === 0
                ? 'Select evaluators to assign'
                : allEvaluators.filter((e) => sel.includes(e.id)).map((e) => e.fullName).join(', ')}
              sx={{ minWidth: 280 }}
            >
              {unassignedOptions.length === 0 && <MenuItem disabled value="">No more evaluators available</MenuItem>}
              {unassignedOptions.map((e) => (
                <MenuItem key={e.id} value={e.id}>{e.fullName} ({e.email})</MenuItem>
              ))}
            </Select>
            <Button variant="contained" onClick={assignEvaluators} disabled={toAdd.length === 0}>Assign</Button>
          </Stack>
        </Paper>
      )}
      {ws && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{ws.tender.reference} — {ws.tender.title}</Typography>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Technical Evaluation" />
          <Tab label="Financial Evaluation" />
          <Tab label="Consolidated Report" />
        </Tabs>
      </Paper>

      {/* TECHNICAL */}
      {tab === 0 && ws && ws.bids.map((bid) => (
        <Paper key={bid.bidId} sx={{ p: 3, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">{bid.vendorName}</Typography>
            <Chip size="small" label={bid.reference} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Table size="small">
            <TableHead><TableRow><TableCell>Criterion</TableCell><TableCell>Weight</TableCell><TableCell>Score (0–100)</TableCell></TableRow></TableHead>
            <TableBody>
              {criteria.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.description}</TableCell>
                  <TableCell>{c.weight}</TableCell>
                  <TableCell>
                    <TextField type="number" size="small" value={bid.technicalScores?.[c.id] ?? ''}
                      onChange={(e) => setScore(bid.bidId, c.id, e.target.value)} inputProps={{ min: 0, max: 100, style: { width: 80 } }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TextField label="Comments" value={bid.technicalComment || ''} onChange={(e) => setComment(bid.bidId, e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => saveTechnical(bid)}>Save Scores</Button>
            {bid.technicalTotal != null && <Typography variant="body2">Weighted total: <strong>{bid.technicalTotal}</strong></Typography>}
          </Stack>
        </Paper>
      ))}

      {/* FINANCIAL */}
      {tab === 1 && ws && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter each vendor's bid amount from their (now unsealed) financial proposal. The comparison and
            value-for-money ranking update automatically.
          </Alert>
          {ws.bids.map((bid) => (
            <Paper key={bid.bidId} sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{bid.vendorName}</Typography>
                <TextField type="number" size="small" label="Bid amount (₦)" value={bid.bidAmount ?? ''} onChange={(e) => setAmount(bid.bidId, e.target.value)} />
                <Button variant="outlined" onClick={() => saveFinancial(bid)}>Save</Button>
              </Stack>
            </Paper>
          ))}

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Comparison</Typography>
          <Paper>
            <Table>
              <TableHead><TableRow>
                <TableCell>Vendor</TableCell><TableCell>Bid Amount</TableCell><TableCell>Technical</TableCell>
                <TableCell>Value for Money</TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {comparison.map((r) => (
                  <TableRow key={r.bidId}>
                    <TableCell>{r.vendorName}</TableCell>
                    <TableCell>{naira(r.bidAmount)}</TableCell>
                    <TableCell>{r.technicalTotal ?? '—'}</TableCell>
                    <TableCell>{r.vfm ?? '—'}</TableCell>
                    <TableCell>{r.lowestPrice && <Chip size="small" color="success" label="Lowest price" />}</TableCell>
                  </TableRow>
                ))}
                {comparison.length === 0 && <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>Enter bid amounts to see the comparison.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* CONSOLIDATED */}
      {tab === 2 && ws && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Consolidated Report</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Technical and financial results summary. Highest value-for-money:{' '}
            <strong>{comparison[0]?.vendorName || '—'}</strong>
          </Typography>

          <Table size="small" sx={{ mb: 3 }}>
            <TableHead><TableRow><TableCell>Vendor</TableCell><TableCell>Technical</TableCell><TableCell>Amount</TableCell><TableCell>VfM</TableCell></TableRow></TableHead>
            <TableBody>
              {comparison.map((r) => (
                <TableRow key={r.bidId}><TableCell>{r.vendorName}</TableCell><TableCell>{r.technicalTotal ?? '—'}</TableCell><TableCell>{naira(r.bidAmount)}</TableCell><TableCell>{r.vfm ?? '—'}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>

          <TextField label="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} fullWidth multiline minRows={3} sx={{ mb: 2 }} />
          <TextField select label="Recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} sx={{ minWidth: 320, mb: 2 }}>
            {RECOMMENDATIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => saveReport(false)}>Save Draft</Button>
            <Button variant="contained" onClick={() => saveReport(true)}>Submit Evaluation Report</Button>
          </Stack>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}>
        <Alert severity="info" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
